
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { Guide } from "@/types/guide";
import { Skeleton } from "@/components/ui/skeleton";
import { BookOpen } from "lucide-react";
import GuideCard from "./GuideCard";
import { useLanguage } from "@/context/LanguageContext";

const LatestGuides = () => {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();

  useEffect(() => {
    const fetchLatestGuides = async () => {
      try {
        const { data, error } = await supabase
          .from('guides')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(3);
          
        if (error) throw error;
        
        if (data) {
          // Process guides based on language
          const processedGuides = data.map(guide => {
            if (language === 'en' && guide.title_en) {
              return {
                ...guide,
                title: guide.title_en,
                summary: guide.summary_en,
                content: guide.content_en,
                tags: guide.tags_en || guide.tags // Use English tags if available
              };
            }
            return guide;
          });
          
          setGuides(processedGuides || []);
        }
      } catch (error) {
        console.error('Error fetching guides:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestGuides();
  }, [language]); // Added language to dependency array

  return (
    <section className="w-full py-12 md:py-16 lg:py-20 bg-gray-50">
      <div className="container px-4 max-w-5xl mx-auto">
        <div className="flex flex-col items-center text-center space-y-6">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
            {language === 'en' ? t('home.latestGuidesTitle') : '📘 最新租屋攻略'}
          </h2>
          <p className="text-lg text-muted-foreground max-w-xl">
            {language === 'en' 
              ? 'Stay updated on rental trends, legal knowledge, and preparation tips'
              : '掌握租屋趨勢、了解法律知識、做好租前準備。'}
          </p>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full mt-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col space-y-3">
                  <Skeleton className="h-48 w-full rounded-lg" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                </div>
              ))}
            </div>
          ) : guides.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full mt-8">
              {guides.map((guide) => (
                <GuideCard key={guide.id} guide={guide} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg text-muted-foreground">
                {language === 'en' ? t('home.noGuides') : '目前尚無租房攻略'}
              </p>
            </div>
          )}
          
          <div className="mt-8">
            <Link to="/guides">
              <Button variant="outline" size="lg">
                {language === 'en' ? t('home.viewMoreGuides') : '查看更多攻略'}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LatestGuides;
